import React, {useEffect, useContext} from "react";
import PropTypes from "prop-types";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useLocation,
  useNavigationType,
  createRoutesFromChildren,
  matchRoutes,
  Outlet
} from "react-router-dom";
import * as Sentry from "@sentry/react";
import {faMapMarkerAlt} from "@fortawesome/free-solid-svg-icons";
import {library} from "@fortawesome/fontawesome-svg-core";
import {APIProvider} from "@vis.gl/react-google-maps";

// Pages
import FacilitiesDashboard from "./pages/FacilitiesDashboard.js";
import FacilityDashboard from "./pages/FacilityDashboard.js";
import FacilityBuilder from "./pages/FacilityBuilder.js";
import ChecksheetBuilder from "./pages/ChecksheetBuilder.js";
import ChecksheetEditor from "./pages/ChecksheetEditor.js";
import ChecksheetPreview from "./pages/ChecksheetPreview.js";
import Checksheet from "./pages/tasks/Checksheet.js";
import ComponentTemplates from "./pages/ComponentTemplates.js";
import ReportBuilder from "./pages/ReportBuilder.js";
import ReportEditor from "./pages/ReportEditor.js";
import Tasks from "./pages/Tasks.js";
import Schedule from "./pages/Schedule.js";
import EventTemplates from "./pages/EventTemplates.js";
import GlobalTables from "./pages/GlobalTables.js";
import Account from "./pages/Account.js";
import UserRoles from "./pages/UserRoles";
import Regions from "./pages/Regions.js";
import Users from "./pages/Users.js";
import Support from "./pages/Support.js";
import Signin from "./pages/SignIn.js";
import FirstSignin from "./pages/FirstSignin.js";
import ForgotPassword from "./pages/ForgotPassword.js";
import ResetPassword from "./pages/ResetPassword.js";
import Unauthorized from "./pages/Unauthorized.js";
import NotFound from "./pages/NotFound.js";
import Maintenance from "./pages/Maintenance.js";
import FacilityNotifications from "./pages/FacilityNotifications.js";
import AppLaunch from "./pages/AppLaunch.js";
import FirstLogin from "./pages/FirstLogin.js";

// Utils
import SettingsProvider from "./contexts/settings.js";
import ToastProvider from "./contexts/toast.js";
import AuthProvider, {AuthContext} from "./contexts/auth.js";
import NotifyProvider from "./contexts/notify.js";
import SocketProvider, {useSocket} from "./contexts/socket.js";
import NavProvider from "./contexts/nav.js";
import FacilityNavProvider from "./contexts/facilitynav.js";
import BannerProvider from "./contexts/banner.js";
import ThemeStateProvider from "./contexts/theme.js";
import CalendarProvider from "./contexts/calendar.js";
import GlobalProvider from "./contexts/global.js";
import {getApiBase} from "./utils/apiBase.js";
import {dropRecoveredDndInvariant} from "./utils/sentry.js";

// Theming
import {GlobalStyle} from "./style/components/general.js";

// Components
import Nav from "./pages/general/Nav.js";
import Protected from "./components/Protected.js";
import FallbackComponent from "./pages/general/FallbackComponent.js";
import {SET_PRESENCE_URL} from "./pages/general/Room.js";

const PresenceLocation = () => {
  const {pathname, search} = useLocation();
  const {currentUser, isAuthenticated} = useContext(AuthContext);
  const socket = useSocket();

  useEffect(() => {
    if (!isAuthenticated || !currentUser?.publicId) return;

    const emitUrl = () => {
      if (socket.connected) {
        socket.emit(SET_PRESENCE_URL, currentUser.publicId, `${pathname}${search}`);
      }
    };

    emitUrl();
    socket.on("connect", emitUrl);

    // eslint-disable-next-line consistent-return
    return () => {
      socket.off("connect", emitUrl);
    };
  }, [pathname, search, currentUser, isAuthenticated, socket]);

  return null;
};

library.add(faMapMarkerAlt);

if (process.env.REACT_APP_SENTRY_DSN)
  Sentry.init({
    release: `hero-builder-client-${process.env.REACT_APP_ENV}`,
    environment: process.env.REACT_APP_ENV || "local",
    dsn: process.env.REACT_APP_SENTRY_DSN,
    integrations: [
      Sentry.reactRouterV6BrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes
      }),
      Sentry.replayIntegration()
    ],
    tracesSampleRate: parseInt(process.env.REACT_APP_SENTRY_TRACE_SAMPLE_RATE, 10),
    tunnel: `${getApiBase()}/tunnel`,
    beforeSend: dropRecoveredDndInvariant
  });

const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes);

const GeneralProtected = ({element}) => (
  <>
    <Nav />
    <Protected element={element} />
  </>
);

GeneralProtected.propTypes = {element: PropTypes.node.isRequired};

const FacilityRoutes = () => {
  const {pathname} = useLocation();
  const isFacilityRoute = pathname !== "/facilities/" && pathname.includes("/facilities/");

  return (
    <FacilityNavProvider>
      <Nav />
      <Protected
        element={isFacilityRoute ? <Outlet /> : <FacilitiesDashboard key="facilities_dashboard" />}
      />
    </FacilityNavProvider>
  );
};

export const AppRoutes = () => (
  <Sentry.ErrorBoundary fallback={FallbackComponent}>
    <SentryRoutes>
      {/* Authentication Routes */}
      <Route path="/signin" element={<Signin />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/first-signin" element={<Protected element={<FirstSignin />} />} />
      <Route path="/first-login" element={<FirstLogin />} />
      <Route path="/app-launch" element={<AppLaunch />} />

      {/* General Routes */}
      <Route
        path="/global-tables"
        element={<GeneralProtected element={<GlobalTables key="global_custom_tables" />} />}
      />
      <Route
        path="/schedule"
        element={
          <GeneralProtected
            element={
              <CalendarProvider>
                <Schedule key="global_schedule" />
              </CalendarProvider>
            }
          />
        }
      />
      <Route
        path="/event-templates"
        element={<GeneralProtected element={<EventTemplates key="event_templates" />} />}
      />
      <Route
        path="/components"
        element={<GeneralProtected element={<ComponentTemplates key="component_templates" />} />}
      />
      <Route path="/account" element={<GeneralProtected element={<Account key="account" />} />} />
      <Route
        path="/users"
        element={<GeneralProtected element={<Users key="user_management" />} />}
      />
      <Route
        path="/user-roles"
        element={<GeneralProtected element={<UserRoles key="user_roles" />} />}
      />
      <Route path="/regions" element={<GeneralProtected element={<Regions key="regions" />} />} />
      <Route path="/support" element={<GeneralProtected element={<Support key="support" />} />} />

      {/* General Unauthorized Fallback */}
      <Route path="/unauthorized" element={<GeneralProtected element={<Unauthorized />} />} />

      {/* Facility Routes */}
      <Route path="/" element={<Navigate to="/facilities" />} />
      <Route path="/facilities" element={<FacilityRoutes />}>
        <Route
          path=":slug"
          element={<Protected element={<FacilityDashboard key="facility_dashboard" />} />}>
          <Route path="analytics" element={<Outlet />} />
          <Route path="checksheets" element={<Outlet />} />
          <Route path="reports" element={<Outlet />} />
          <Route path="tables" element={<Outlet />} />
          <Route path="users" element={<Outlet />} />
        </Route>
        <Route
          path=":slug/builder"
          element={<Protected element={<FacilityBuilder key="facility_builder" />} />}
        />
        <Route
          path=":slug/checksheet-builder"
          element={<Protected element={<ChecksheetBuilder key="checksheet_builder" />} />}
        />
        <Route
          path=":slug/checksheets/:edit/edit"
          element={<Protected element={<ChecksheetEditor key="checksheet_editor" />} />}
        />
        <Route
          path=":slug/checksheets/:edit/preview"
          element={<Protected element={<ChecksheetPreview key="checksheet_preview" />} />}
        />
        <Route path=":slug/tasks" element={<Protected element={<Tasks key="tasks" />} />} />
        <Route
          path=":slug/tasks/:checksheetSlug"
          element={<Protected element={<Checksheet key="checksheet_id" />} />}
        />
        <Route
          path=":slug/schedule"
          element={
            <Protected
              element={
                <CalendarProvider>
                  <Schedule key="schedule" />
                </CalendarProvider>
              }
            />
          }
        />
        <Route
          path=":slug/report-builder"
          element={<Protected element={<ReportBuilder key="report_builder" />} />}
        />
        <Route
          path=":slug/reports/:edit/edit"
          element={<Protected element={<ReportEditor key="report_editor" />} />}
        />
        <Route
          path=":slug/notifications"
          element={<Protected element={<FacilityNotifications key="facility_notifications" />} />}
        />
      </Route>

      {/* General Not Found Fallback */}
      <Route path="/*" element={<Protected element={<NotFound />} />} />

      {/* Maintenance Page */}
      <Route path="/maintenance" element={<Protected element={<Maintenance />} />} />
    </SentryRoutes>
  </Sentry.ErrorBoundary>
);

const App = () => (
  <ThemeStateProvider>
    <GlobalStyle />

    <APIProvider apiKey={process.env.REACT_APP_GOOGLE_KEY}>
      <SocketProvider>
        <ToastProvider>
          <GlobalProvider>
            <SettingsProvider>
              <AuthProvider>
                <NotifyProvider>
                  {/* v7_startTransition is necessary for the new router to work with the old router https://reactrouter.com/v6/upgrading/future#v7_starttransition. */}
                  {/* v7_relativeSplatPath is necessary for the new router to work with the old router https://reactrouter.com/v6/upgrading/future#v7_relativesplatpath. */}
                  <BrowserRouter future={{v7_startTransition: true, v7_relativeSplatPath: true}}>
                    <BannerProvider>
                      <NavProvider>
                        <PresenceLocation />
                        <AppRoutes />
                      </NavProvider>
                    </BannerProvider>
                  </BrowserRouter>
                </NotifyProvider>
              </AuthProvider>
            </SettingsProvider>
          </GlobalProvider>
        </ToastProvider>
      </SocketProvider>
    </APIProvider>
  </ThemeStateProvider>
);

export default App;
